import {HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom, fromEvent, merge, Observable, Subject, Subscription} from 'rxjs';
import {take, takeUntil, timeout} from 'rxjs/operators';
import {MaintenanceStatus} from '../interfaces/Maintenance';

/** One request lane for startup, timer, focus and phase boundaries. Never retries in a tight loop. */
export class MaintenanceStatusPoller {
  private readonly subscriptions = new Subscription();
  private readonly destroyed$ = new Subject<void>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private inFlight: Promise<void> | null = null;
  private started = false;
  private disposed = false;
  private failures = 0;
  private nextAllowedAt = 0;
  private dueAt = 0;

  constructor(private readonly request: () => Observable<MaintenanceStatus>,
              private readonly onStatus: (status: MaintenanceStatus) => void | boolean) {}

  initialize(): Promise<void> {
    return this.refresh(true);
  }

  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;
    this.subscriptions.add(merge(fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange'))
      .subscribe(() => {
        if (document.hidden) this.clearTimer();
        else void this.refresh();
      }));
    this.schedule();
  }

  /** API maintenance refusals may provide a newer schedule than an outstanding status request. */
  observe(status: MaintenanceStatus): void {
    if (this.disposed) return;
    this.plan(status);
    this.schedule();
  }

  private refresh(allowHidden = false): Promise<void> {
    if (this.disposed) return Promise.resolve();
    if (this.inFlight) return this.inFlight;
    if ((!allowHidden && document.hidden) || performance.now() < this.nextAllowedAt) {
      this.schedule();
      return Promise.resolve();
    }
    this.clearTimer();
    this.nextAllowedAt = performance.now() + 2000;
    // Starting in a microtask also puts synchronous request-factory failures through the same backoff.
    this.inFlight = Promise.resolve().then(() => {
      if (this.disposed) throw new Error('Maintenance poller disposed');
      return firstValueFrom(this.request().pipe(timeout(10000), take(1), takeUntil(this.destroyed$)));
    }).then(status => {
      if (this.disposed) return;
      this.failures = 0;
      if (this.onStatus(status) !== false) this.plan(status);
      else this.dueAt = this.nextAllowedAt;
    }).catch((failure: unknown) => {
      if (this.disposed) return;
      this.failures = Math.min(8, this.failures + 1);
      const base = Math.min(300000, 5000 * 2 ** (this.failures - 1));
      const backoff = Math.min(300000, base + Math.random() * Math.min(5000, base * 0.2));
      const retryAfter = this.retryDelay(failure);
      this.nextAllowedAt = performance.now() + Math.max(backoff, retryAfter);
      this.dueAt = this.nextAllowedAt;
    }).finally(() => {
      this.inFlight = null;
      this.schedule();
    });
    return this.inFlight;
  }

  private plan(status: MaintenanceStatus): void {
    const now = performance.now();
    const serverNow = Date.parse(status.serverNow);
    this.dueAt = now + 30000 + Math.random() * 5000;
    if (!Number.isFinite(serverNow)) return;
    const dates = [status.notifyFrom, status.startAt, status.endAt]
      .filter((value): value is string => !!value).map(value => Date.parse(value) + 50)
      .filter(value => Number.isFinite(value) && value > serverNow);
    if (dates.length) this.dueAt = Math.min(this.dueAt, now + Math.min(...dates) - serverNow);
  }

  private retryDelay(failure: unknown): number {
    if (!(failure instanceof HttpErrorResponse)) return 0;
    const value = failure.headers.get('Retry-After')?.trim();
    if (!value) return 0;
    const seconds = Number(value);
    const delay = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : Date.parse(value) - Date.now();
    return Number.isFinite(delay) ? Math.max(0, delay) : 0;
  }

  private schedule(): void {
    this.clearTimer();
    if (!this.started || this.disposed || this.inFlight || document.hidden) return;
    const delay = Math.max(0, this.dueAt - performance.now(), this.nextAllowedAt - performance.now());
    this.timer = setTimeout(() => void this.refresh(), Math.min(delay, 2147483647));
  }

  private clearTimer(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  destroy(): void {
    this.disposed = true;
    this.clearTimer();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.subscriptions.unsubscribe();
  }
}
